import {
	JobService, NoteService, LocationService, PostService, InvoiceEstimatesService,
} from '@services';
import {
	isNull,
	isUndefined,
	map,
	findIndex,
	forEach,
	parseInt,
	isEmpty,
} from 'lodash';
import axios from 'axios';
import { push } from 'react-router-redux';
import { v4 as uuid } from 'uuid';
import queryString from 'query-string';
import moment from 'moment';
import utils, { constants } from '@utils';
import reduxHelpers from '@redux-core/helpers';
import AppActions from '@redux/app';
import Actions, { INITIAL_STATE as jobsInitialState } from '.';

const initialDateRangeByViewType = {
	needApproval : 7,
	all          : 7,
	active       : 7,
	upcoming     : 7,
	unstaffed    : 7,
	completed    : 7,
};

let cancel;

/* ------------- Thunks actions ------------- */
export const getJobs = () => ((dispatch, getState) => {
	const {
		router : {
			location : { pathname },
		},
		jobs : {
			filter : {
				locationId,
				dateRange,
				pageNumber,
				displayCount,
				includeInactive,
				unStaffed,
				deleted,
				sortBy,
				asc,
				recurrenceId,
				startDate,
				endDate,
				prePay,
				type : status,
			},
		},
	} = getState();

	// If there is a request already running, abort it and start a new one
	if (!isUndefined(cancel)) cancel();

	const viewType = pathname.split('/')[3] || 'active';

	// if custom date range is selected and date constraints are not provided
	// we should not fetch the job list
	if (dateRange === 'custom' && !(startDate && endDate)) return;

	const jobType = constants.JOB_TYPES[viewType];
	const params = {
		locationId   : locationId !== null ? locationId : undefined,
		recurrenceId : recurrenceId !== null ? recurrenceId : undefined,
		dateRange,
		pageNumber,
		displayCount,
		includeInactive,
		unStaffed,
		deleted,
		sortBy,
		asc,
		status,
		startDate,
		endDate,
		prePay,
	};

	if (viewType === 'needApproval') {
		dispatch(Actions.receiveData([{ property : 'jobEstimates', value : [] }]));
		dispatch(Actions.updateLoaders('jobEstimates', true));

		InvoiceEstimatesService.getAll({
			status,
			locationId,
			pageNumber,
			displayCount,
			asc,
			getCancelToken : (c) => { cancel = c; },
		})
			.then(({
				data : {
					ItemList, Count, Page, DisplayCount,
				},
			}) => {
				cancel = undefined;

				const filtersCollection = [];

				// Receive data
				dispatch(Actions.receiveData([
					{ property : 'jobEstimates', value : ItemList },
					{ property : 'totalCount', value : Count },
					{ property : ['counts', jobType.name], value : Count },
				]));

				/**
				 * Update pageNumber
				 * if it's coming back
				 * in response
				 */
				if (!isUndefined(Page)) filtersCollection.push({ property : 'pageNumber', value : Page });

				/**
				 * Update displayCount
				 * if it's coming back
				 * in response
				 */
				if (!isUndefined(DisplayCount)) filtersCollection.push({ property : 'displayCount', value : DisplayCount });

				// Update related filters
				dispatch(Actions.updateFilters(filtersCollection));

				// Stop loading animation
				dispatch(Actions.updateLoaders('jobEstimates', false));

				// scroll to top page
				window.scrollTo(0, 0);
			})
			.catch((error) => {
				if (!axios.isCancel(error)) {
					reduxHelpers.apiErrorHandler({
						dispatch,
						module         : 'jobs',
						property       : 'loaders.jobEstimates',
						defaultMessage : 'Error loading jobs',
					})(error);
				}
			});

		return;
	}

	// if dateRange is tomorrow/yesterday
	// need to set the startDate and endDate accordingly
	if (dateRange === 2) {
		let date = moment();

		if (status === constants.JOB_TYPES.upcoming.type) date = date.add(1, 'day').format('YYYY-MM-DD');
		else if (status === constants.JOB_TYPES.completed.type) date = date.subtract(1, 'day').format('YYYY-MM-DD');

		params.startDate = date;
		params.endDate = date;
	}

	dispatch(Actions.clearJobs());

	// Start loading animation
	dispatch(Actions.updateLoaders('jobs', true));

	JobService.getAll({
		...params,
		getCancelToken : (c) => { cancel = c; },
	})
		.then(({
			data : {
				ItemList, Count, Page, DisplayCount,
			},
		}) => {
			cancel = undefined;

			const filtersCollection = [];

			// Receive data
			dispatch(Actions.receiveData([
				{ property : 'jobs', value : ItemList },
				{ property : 'totalCount', value : Count },
				{ property : ['counts', jobType.name], value : Count },
			]));

			if (!isUndefined(Page)) filtersCollection.push({ property : 'pageNumber', value : Page });

			if (!isUndefined(DisplayCount)) filtersCollection.push({ property : 'displayCount', value : DisplayCount });

			// Update related filters
			dispatch(Actions.updateFilters(filtersCollection));

			// Stop loading animation
			dispatch(Actions.updateLoaders('jobs', false));

			// scroll to top page
			window.scrollTo(0, 0);
		})
		.catch((error) => {
			if (!axios.isCancel(error)) {
				reduxHelpers.apiErrorHandler({
					dispatch,
					module         : 'jobs',
					property       : 'loaders.jobs',
					defaultMessage : 'Error loading jobs',
				})(error);
			}
		});
});

export const getJobEventNotes = () => ((dispatch, getState) => {
	const {
		jobs : {
			jobEvent : {
				ClientId : clientId,
				ProjectId : projectId,
				LocationId : locationId,
			},
			notes : {
				PagingToken,
			},
		},
	} = getState();
	// Start loading animation
	dispatch(Actions.updateLoaders('notes', true));

	NoteService.getAll({
		clientId,
		projectId,
		locationId,
		displayCount : 10,
		pagingToken  : !isNull(PagingToken) ? PagingToken : undefined,
	})
		.then(({ data }) => {
			dispatch(Actions.receiveNotes(data));

			// Stop loading animation
			dispatch(Actions.updateLoaders('notes', false));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module         : 'jobs',
			property       : 'loaders.notes',
			defaultMessage : 'Error getting Job Activity Timeline',
		}));
});

export const loadJobEvent = (jobId) => ((dispatch) => {
	// Start loading animation
	dispatch(Actions.updateLoaders('details', true));

	JobService.getById(jobId)
		.then(({ data }) => {
			// Receive jobEvent
			dispatch(Actions.receiveJobEvent(data));

			// Load related data
			dispatch(getJobEventNotes());

			// Stop loading animation
			dispatch(Actions.updateLoaders('details', false));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module         : 'jobs',
			property       : 'loaders.details',
			defaultMessage : 'Error loading job',
		}));
});

export const getJobHistory = (jobId) => ((dispatch) => {
	// Start loading animation
	dispatch(Actions.updateLoaders('jobHistoryModal', true));

	JobService.getJobHistoryById(jobId)
		.then(({ data }) => {
			dispatch(Actions.receiveJobHistory(data));

			// Stop loading animation
			dispatch(Actions.updateLoaders('jobHistoryModal', false));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module         : 'jobs',
			property       : 'loaders.details',
			defaultMessage : 'Error loading job history',
		}));
});

export const getLocations = () => ((dispatch) => {
	// Start loading animation
	dispatch(Actions.updateLoaders('locations', true));

	LocationService.getAll({
		minimalData : true,
	})
		.then(({ data }) => {
			const locations = map(data, ({
				Id : value, Name : label, Latitude : lat, Longitude : lng, OrganizationFee : organizationFee,
			}) => ({
				value,
				label,
				coordinates : { lat, lng },
				organizationFee,
			}));

			// Receive data
			dispatch(Actions.receiveData([{ property : 'locations', value : locations }]));

			// Stop loading animation
			dispatch(Actions.updateLoaders('locations', false));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module         : 'jobs',
			property       : 'loaders.locations',
			defaultMessage : 'Error loading locations',
		}));
});

export const getJobPoints = (jobId, userId) => ((dispatch, getState) => {
	const { activeMapJob } = getState().jobs;

	dispatch(Actions.updateLoaders('jobPoints', true));

	JobService.getCheckIns(jobId || activeMapJob, userId)
		.then(({ data }) => {
			const checkins = data.CheckIns.filter((item) => item.IsCheckin).map((item) => ({ ...item, type : 'checkin', name : 'Clocked In' }));
			const checkouts = data.CheckIns
				.filter((item) => item.CheckoutLatitude !== null)
				.map((item) => ({ ...item, type : 'checkout', name : 'Clocked Out' }));
			const pings = data.CheckIns.filter((item) => item.Type === 1).map((item) => ({ ...item, type : 'ping', name : 'Ping' }));

			const options = [{ value : 'all', label : 'All' }];
			if (!isEmpty(checkins) || !isEmpty(checkouts)) options.push({ value : 'checkin', label : 'Clock-in/out', color : 'yellow' });
			if (!isEmpty(pings)) options.push({ value : 'ping', label : 'Pings', color : 'blue' });

			const jobData = {
				name          : data.JobLocation.Name,
				startDateTime : data.StartDateTime,
				endDateTime   : data.EndDateTime,
				location      : { lat : data.JobLocation.Latitude, lng : data.JobLocation.Longitude },
			};

			const pointsArray = [
				{
					id : uuid(), type : 'job', name : jobData.name, location : jobData.location,
				},
				...checkins,
				...pings,
				...checkouts,
			];

			const points = pointsArray.map((item) => {
				if (item.type === 'job') return item;

				return {
					id       : uuid(),
					type     : item.type,
					name     : item.name,
					dateTime : item.type === 'checkout' ? item.CheckoutDateTimeUTC : item.CheckinDateTimeUTC,
					location : item.type === 'checkout'
						? { lat : item.CheckoutLatitude, lng : item.CheckoutLongitude }
						: { lat : item.Latitude, lng : item.Longitude },
					distance : item.DistanceFromJob,
				};
			});

			dispatch(Actions.receiveData([
				{ property : 'locationDetails.points', value : points },
				{ property : 'locationDetails.options', value : options },
				{ property : 'locationDetails.job', value : jobData },
				{ property : 'locationDetails.mapCenter', value : jobData.location },
			]));

			dispatch(Actions.updateLoaders('jobPoints', false));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module         : 'jobs',
			property       : 'loaders.jobPoints',
			defaultMessage : 'Error loading job points',
		}));
});

export const getOfficerJobFeeds = (jobId, officerId) => ((dispatch, getState) => {
	dispatch(Actions.updateLoaders('locationDetailsFeed', true));
	PostService.getAllByJobId({
		jobId,
		officerId,
		pagingToken : getState().jobs.locationDetails.feed.pagingToken,
	})
		.then(({ data }) => {
			dispatch(Actions.updateLoaders('locationDetailsFeed', false));

			const currentFeed = getState().jobs.locationDetails.feed.list;

			dispatch(Actions.receiveData([
				{ property : 'locationDetails.feed.list', value : [...currentFeed, ...data.ItemList] },
				{ property : 'locationDetails.feed.pagingToken', value : data.PagingToken },
				{ property : 'locationDetails.feed.hasNext', value : data.HasNext },
			]));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module   : 'jobs',
			property : 'loading.officerFeed',
		}));
});

export const getCounts = () => ((dispatch, getState) => {
	const {
		filter : {
			locationId,
			dateRange,
			includeInactive,
			unStaffed,
			deleted,
			recurrenceId,
			startDate,
			endDate,
			prePay,
		},
	} = getState().jobs;

	// Start loading animation
	dispatch(Actions.updateLoaders('counts', true));

	JobService.getCounts({
		locationId,
		dateRange,
		includeInactive,
		unStaffed,
		deleted,
		recurrenceId,
		startDate,
		endDate,
		prePay,
	})
		.then(({ data }) => {
			// Receive data
			dispatch(Actions.receiveData([{ property : 'counts', value : data }]));

			// Stop loading animation
			dispatch(Actions.updateLoaders('counts', false));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module         : 'jobs',
			property       : 'loaders.counts',
			defaultMessage : 'Error loading job counts',
		}));
});

export const processUrl = (loadCounters = false) => ((dispatch, getState) => {
	const {
		router : {
			location : {
				pathname,
				search,
			},
		},
		jobs : {
			skipJobsRequest,
		},
	} = getState();

	const query = queryString.parse(search);

	const viewType = pathname.split('/')[3] || 'active';
	const jobId = pathname.split('/')[4];

	const jobType = constants.JOB_TYPES[viewType];
	const activeLink = findIndex(constants.JOB_TYPES_MAP, ['viewType', viewType]);

	const dataCollection = [];

	const filtersCollection = [
		{ property : 'type', value : jobType.type },
		{ property : 'dateRange', value : initialDateRangeByViewType[viewType] },
	];

	if (jobType.countKey === constants.JOB_TYPES.unstaffed.countKey) filtersCollection.push({ property : 'unStaffed', value : true });
	else filtersCollection.push({ property : 'unStaffed', value : false });

	forEach(query, (rawValue, key) => {
		let property;
		let value = rawValue;

		switch (key) {
			default:
				property = key;
				break;

			case 'page':
				property = 'pageNumber';
				break;

			case 'show':
				property = 'displayCount';
				break;

			case 'calendarView':
			case 'startDate':
			case 'endDate':
			case 'dateRange': {
				property = key;

				const filterIndex = findIndex(filtersCollection, ['property', property]);

				if (filterIndex > -1) filtersCollection.splice(filterIndex, 1);
				break;
			}
		}

		// Parse booleans
		if (value === 'true' || value === 'false') value = value === 'true';

		// Parse integers
		if (
			key !== 'startDate'
			&& key !== 'endDate'
			&& !Number.isNaN(parseInt(value))
		) value = parseInt(value);

		filtersCollection.push({ property, value });
	});

	if (!isUndefined(jobId)) {
		// Set activeMapJob
		dataCollection.push({ property : 'activeMapJob', value : parseInt(jobId) });

		// Clear data as well
		dataCollection.push({ property : 'jobClockIns', value : [] });
		dataCollection.push({ property : 'jobClockOuts', value : [] });
		dataCollection.push({ property : 'jobPings', value : [] });
	}

	// Reset expandedRowIds
	dataCollection.push({ property : 'expandedRowIds', value : [] });

	if (skipJobsRequest) dataCollection.push({ property : 'skipJobsRequest', value : false });

	dispatch(Actions.updateFilters(filtersCollection));

	// Apply updated data
	dispatch(Actions.receiveData(dataCollection));

	// Set Active link
	dispatch(AppActions.updateActiveLink('subMenu', activeLink));

	if (!skipJobsRequest) {
		dispatch(getJobs());
		dispatch(getLocations());
	}

	if (!skipJobsRequest && loadCounters) dispatch(getCounts());
});

export const applyCriteria = (criteriaCollection) => ((dispatch, getState) => {
	const {
		router : {
			location : {
				pathname : rawPathname,
				search,
			},
			...router
		},
		jobs   : {
			filter : {
				sortBy,
				asc,
			},
		},
	} = getState();
	let viewType = 'active';
	let jobId;
	if (router && router.match && router.match.params) {
		({ match : { params : { viewType, id : jobId } } } = router);
	}

	const { filter : initialFilter } = jobsInitialState;
	const query = !isUndefined(search) ? queryString.parse(search) : {};
	const filtersCollection = [];
	const dataCollection = [];
	let pathname = rawPathname;

	// Process criteriaCollection
	forEach(criteriaCollection, ({ property, value }) => {
		// Add criteria to filters
		filtersCollection.push({
			property,
			value,
		});

		// Update query param
		query[property] = value;

		switch (property) {
			case 'pageNumber': {
				delete query.pageNumber;
				query.page = value;

				if (value === initialFilter.pageNumber) delete query.page;
				break;
			}

			case 'displayCount': {
				delete query.displayCount;
				query.show = value;

				if (value === initialFilter.displayCount) delete query.show;
				break;
			}

			case 'dateRange': {
				if (value === initialDateRangeByViewType[viewType]) delete query.dateRange;

				if (value !== 'custom') {
					delete query.startDate;
					delete query.endDate;

					filtersCollection.push({
						property : 'startDate',
						value    : '',
					});
					filtersCollection.push({
						property : 'endDate',
						value    : '',
					});
				}

				break;
			}

			case 'sortBy': {
				const newDirection = sortBy === value ? !asc : true;
				query.asc = newDirection;

				if (value === initialFilter.sortBy) delete query.sortBy;

				if (newDirection) delete query.asc;

				filtersCollection.push({
					property : 'asc',
					value    : newDirection,
				});
				break;
			}

			case 'jobId': {
				delete query.jobId;

				pathname = `/jobs/map/${viewType}${value !== parseInt(jobId) ? `/${value}` : ''}`;

				filtersCollection.splice(0, 1);

				dataCollection.push({
					property : 'activeMapJob',
					value    : value !== parseInt(jobId) ? value : '',
				});

				dataCollection.push({
					property : 'jobClockIns',
					value    : [],
				});
				dataCollection.push({
					property : 'jobClockOuts',
					value    : [],
				});
				dataCollection.push({
					property : 'jobPings',
					value    : [],
				});
				dataCollection.push({
					property : 'skipJobsRequest',
					value    : true,
				});
				break;
			}

			default:
				break;
		}

		if (property !== 'pageNumber') {
			delete query.page;
			filtersCollection.push({
				property : 'pageNumber',
				value    : 1,
			});
		}

		if (
			(
				property !== 'pageNumber'
				|| property !== 'displayCount'
				|| property !== 'sortBy'
			)
			&& !value
		) delete query[property];
	});

	// Save filters
	dispatch(Actions.updateFilters(filtersCollection));

	// Apply updated data
	dispatch(Actions.receiveData(dataCollection));

	// Update URL
	dispatch(push({
		pathname,
		search : queryString.stringify(query),
	}));
});

export const goToJob = (jobId) => ((dispatch) => {
	// if no jobId exists, the user did something funny or cleared a search field
	// Don't take any action, keep whatever is on the screen displayed
	if (!jobId || jobId === '') {
		return;
	}
	const id = parseInt(jobId, 10);
	// 2147483647 is the maximum integer
	// supported by the API, any number greater
	// than this will produce a bad request.
	if (typeof id !== 'number' || id > 2147483647) {
		utils.notify({
			type    : 'error',
			message : 'Invalid Job ID',
		});

		return;
	}
	// Start loading animation
	dispatch(Actions.updateLoaders('jobs', true));

	JobService.getById(jobId)
		.then(() => {
			// Redirect to jobView
			dispatch(push(`/jobs/${jobId}`));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module         : 'jobs',
			property       : 'loaders.jobs',
			defaultMessage : 'Job not found',
		}));
});

export const getApplicantsByPositionId = (positionId) => ((dispatch) => {
	// Start loading animation
	dispatch(Actions.updateLoaders('applicants', true));

	JobService.getApplicantsByPositionId(positionId)
		.then(({ data }) => {
			dispatch(Actions.receivePositionData('applicants', positionId, data));

			// Stop loading animation
			dispatch(Actions.updateLoaders('applicants', false));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module         : 'jobs',
			property       : 'loaders.applicants',
			defaultMessage : 'Error loading applicants',
		}));
});

export const getAssignedOfficersByPositionId = (positionId) => ((dispatch) => {
	// Start loading animation
	dispatch(Actions.updateLoaders('officers', true));

	JobService.getAssignedOfficersByPositionId(positionId)
		.then(({ data }) => {
			dispatch(Actions.receivePositionData('officers', positionId, data));

			// Stop loading animation
			dispatch(Actions.updateLoaders('officers', false));
		})
		.catch(reduxHelpers.apiErrorHandler({
			dispatch,
			module         : 'jobs',
			property       : 'loaders.officers',
			defaultMessage : 'Error loading Assigned Officers',
		}));
});

export default {
	getJobs,
	loadJobEvent,
	getJobEventNotes,
	getLocations,
	getCounts,
	processUrl,
	applyCriteria,
	goToJob,
	getApplicantsByPositionId,
	getAssignedOfficersByPositionId,
	getJobPoints,
	getOfficerJobFeeds,
	getJobHistory,
};
